/** Sky-level painters: gradient sky, sun, rainbow and slanting light rays. */
import type { LightRaysLayer, RainbowLayer, SkyLayer, SunLayer } from '@dropstack/core';
import { clamp } from '../layout.ts';
import { circlePath, glow, gloss, rgba, shade, strokeRim, vGradient } from './draw.ts';
import type { Painter } from './types.ts';

export const skyPainter: Painter<SkyLayer> = {
  still(ctx, layer, { palette, size }) {
    const { width, height, bleed } = size;
    const stops: [number, string][] = [[0, palette.skyTop]];
    if (layer.mid) stops.push([clamp(layer.midAt ?? 0.55, 0.05, 0.95), layer.mid]);
    stops.push([1, palette.skyBottom]);
    ctx.fillStyle = vGradient(ctx, 0, height, stops);
    ctx.fillRect(-bleed, 0, width + bleed * 2, height);
    if (layer.glow) {
      const g = layer.glow;
      glow(ctx, g.x * width, g.y * height, g.radius * Math.max(width, height), g.color, 0.85);
    }
  },
};

/** Rays turn one full circle in about four minutes. */
const SUN_TURN_RATE = 0.026;

export const sunPainter: Painter<SunLayer> = {
  // The rays sit behind the disc, so a rayed sun is painted as one moving layer.
  moving(ctx, layer, { size, time }) {
    const x = layer.x * size.width;
    const y = layer.y * size.height;
    const r = layer.radius * size.unit;
    glow(ctx, x, y, r * 3.4, layer.glow, 0.9);

    const rays = Math.max(0, Math.floor(layer.rays ?? 0));
    if (rays > 0) {
      const color = layer.rayColor ?? layer.glow;
      const turn = time * SUN_TURN_RATE;
      const breathe = 1 + 0.04 * Math.sin(time * 0.6);
      for (let i = 0; i < rays; i++) {
        const a = turn + (i / rays) * Math.PI * 2;
        const long = i % 2 === 0 ? 2.5 : 1.95;
        const half = (Math.PI / rays) * 0.42;
        const inner = r * 1.12;
        const outer = r * long * breathe;
        ctx.beginPath();
        ctx.moveTo(x + Math.cos(a - half) * inner, y + Math.sin(a - half) * inner);
        ctx.quadraticCurveTo(x + Math.cos(a) * outer * 1.08, y + Math.sin(a) * outer * 1.08, x + Math.cos(a + half) * inner, y + Math.sin(a + half) * inner);
        ctx.closePath();
        const g = ctx.createRadialGradient(x, y, inner, x, y, outer);
        g.addColorStop(0, rgba(color, 0.75));
        g.addColorStop(1, rgba(color, 0));
        ctx.fillStyle = g;
        ctx.fill();
      }
    }

    // The disc: warm gradient, soft rim and a glossy highlight.
    ctx.beginPath();
    circlePath(ctx, x, y, r);
    const body = ctx.createRadialGradient(x - r * 0.3, y - r * 0.35, r * 0.1, x, y, r);
    body.addColorStop(0, shade(layer.color, 0.45));
    body.addColorStop(0.7, layer.color);
    body.addColorStop(1, shade(layer.color, -0.06));
    ctx.fillStyle = body;
    ctx.fill();
    strokeRim(ctx, shade(layer.color, -0.18), size, 1.2);
    gloss(ctx, x - r * 0.35, y - r * 0.4, r * 0.32, r * 0.18, 0.5, -0.6);
  },
  animated: (layer) => (layer.rays ?? 0) > 0,
};

/** Soft pastel bands, outermost first (red … violet). */
const RAINBOW = ['#ff8a8a', '#ffc078', '#ffe066', '#8ce99a', '#74c0fc', '#b197fc'];

function rainbowArc(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, width: number, alpha: number, reversed: boolean): void {
  const bands = reversed ? [...RAINBOW].reverse() : RAINBOW;
  const band = width / bands.length;
  bands.forEach((color, i) => {
    const r = radius - band * (i + 0.5);
    if (r <= 0) return;
    ctx.beginPath();
    ctx.arc(x, y, r, Math.PI, Math.PI * 2);
    ctx.strokeStyle = rgba(color, alpha);
    ctx.lineWidth = band + 0.6; // slight overlap: no hairline gaps between bands
    ctx.stroke();
  });
  // A faint white sheen on the inside edge makes it glow rather than sit flat.
  ctx.beginPath();
  ctx.arc(x, y, Math.max(0, radius - width - band * 0.6), Math.PI, Math.PI * 2);
  ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.5})`;
  ctx.lineWidth = band * 1.2;
  ctx.stroke();
}

export const rainbowPainter: Painter<RainbowLayer> = {
  still(ctx, layer, { size }) {
    const x = layer.x * size.width;
    const y = layer.y * size.height;
    const radius = layer.radius * size.unit;
    const width = layer.width * size.unit;
    const alpha = clamp(layer.opacity ?? 0.6, 0, 1);
    ctx.lineCap = 'butt';
    if (layer.double) rainbowArc(ctx, x, y, radius * 1.28, width * 0.7, alpha * 0.45, true);
    rainbowArc(ctx, x, y, radius, width, alpha, false);
  },
};

export const lightRaysPainter: Painter<LightRaysLayer> = {
  moving(ctx, layer, { size, time, seed }) {
    const { width, height } = size;
    const ox = layer.x * width;
    const oy = -height * 0.12;
    const count = Math.max(1, Math.floor(layer.count));
    const reach = height * 1.05;
    for (let i = 0; i < count; i++) {
      const t = count === 1 ? 0.5 : i / (count - 1);
      const a = Math.PI / 2 + (t - 0.5) * layer.spread;
      const shimmer = 0.55 + 0.45 * Math.sin(time * (0.35 + (i % 3) * 0.07) + i * 1.7 + (seed % 7));
      const alpha = clamp(layer.opacity, 0, 1) * shimmer;
      const half = (layer.spread / count) * (0.18 + 0.12 * ((i * 7) % 3));
      const ex1 = ox + Math.cos(a - half) * reach;
      const ey1 = oy + Math.sin(a - half) * reach;
      const ex2 = ox + Math.cos(a + half) * reach;
      const ey2 = oy + Math.sin(a + half) * reach;
      const g = ctx.createLinearGradient(ox, oy, ox + Math.cos(a) * reach, oy + Math.sin(a) * reach);
      g.addColorStop(0, rgba(layer.color, alpha));
      g.addColorStop(0.6, rgba(layer.color, alpha * 0.45));
      g.addColorStop(1, rgba(layer.color, 0));
      ctx.beginPath();
      ctx.moveTo(ox, oy);
      ctx.lineTo(ex1, ey1);
      ctx.lineTo(ex2, ey2);
      ctx.closePath();
      ctx.fillStyle = g;
      ctx.fill();
    }
  },
};
